import React, { useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BetaSignup = () => {
  const [formData, setFormData] = useState({
    organizationName: '',
    contactName: '',
    email: '',
    phone: '',
    organizationType: '',
    bedCount: '',
    challenges: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/beta-applications`, formData);
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong submitting your application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (submitted) {
    return (
      <div className="py-20 text-center">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-8">
            <div className="text-6xl mb-4">✅</div>
            <h1 className="text-3xl font-bold text-green-800 mb-4">
              Application Submitted!
            </h1>
            <p className="text-lg text-green-700 mb-6">
              Thank you for your interest in Shelter Care Connect beta testing.
            </p>
            <p className="text-green-600">
              We'll review your application and contact you within 48 hours.
            </p>
            <div className="mt-8">
              <button
                onClick={() => setSubmitted(false)}
                className="text-green-600 hover:text-green-800 underline"
              >
                Submit another application
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12">
      {/* Header */}
      <section className="max-w-4xl mx-auto px-4 text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">
          Join Our Beta Testing Program
        </h1>
        <p className="text-xl text-gray-600">
          Get FREE access for 3 months and help shape the future of shelter management
        </p>
      </section>

      {/* Benefits */}
      <section className="max-w-6xl mx-auto px-4 mb-12">
        <div className="grid md:grid-cols-4 gap-6">
          <div className="bg-green-50 p-6 rounded-lg text-center">
            <div className="text-2xl font-bold text-green-600 mb-2">100%</div>
            <div className="text-green-800 font-semibold">Free</div>
            <div className="text-sm text-green-700">Complete system</div>
          </div>
          <div className="bg-blue-50 p-6 rounded-lg text-center">
            <div className="text-2xl font-bold text-blue-600 mb-2">3</div>
            <div className="text-blue-800 font-semibold">Months</div>
            <div className="text-sm text-blue-700">Full access</div>
          </div>
          <div className="bg-purple-50 p-6 rounded-lg text-center">
            <div className="text-2xl font-bold text-purple-600 mb-2">24/7</div>
            <div className="text-purple-800 font-semibold">Support</div>
            <div className="text-sm text-purple-700">Dedicated team</div>
          </div>
          <div className="bg-orange-50 p-6 rounded-lg text-center">
            <div className="text-2xl font-bold text-orange-600 mb-2">10</div>
            <div className="text-orange-800 font-semibold">Partners</div>
            <div className="text-sm text-orange-700">Limited spots</div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="max-w-3xl mx-auto px-4">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h2 className="text-2xl font-bold mb-6">Beta Application Form</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization Name *
                </label>
                <input
                  type="text"
                  name="organizationName"
                  required
                  value={formData.organizationName}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Name *
                </label>
                <input
                  type="text"
                  name="contactName"
                  required
                  value={formData.contactName}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization Type *
                </label>
                <select
                  name="organizationType"
                  required
                  value={formData.organizationType}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select type...</option>
                  <option value="emergency-shelter">Emergency Shelter</option>
                  <option value="transitional-housing">Transitional Housing</option>
                  <option value="family-shelter">Family Shelter</option>
                  <option value="day-center">Day Center</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Beds *
                </label>
                <select
                  name="bedCount"
                  required
                  value={formData.bedCount}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select range...</option>
                  <option value="1-25">1-25 beds</option>
                  <option value="26-50">26-50 beds</option>
                  <option value="51-100">51-100 beds</option>
                  <option value="101-200">101-200 beds</option>
                  <option value="200+">200+ beds</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What's your biggest operational challenge? *
              </label>
              <textarea
                name="challenges"
                required
                rows={4}
                value={formData.challenges}
                onChange={handleChange}
                placeholder="Describe your main operational challenge..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-2">What happens next:</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• We'll review your application within 48 hours</li>
                <li>• If selected, we'll schedule a consultation call</li>
                <li>• Beta testing begins with full training and support</li>
              </ul>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="beta-error">{error}</div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="beta-submit-btn"
              className="w-full bg-green-500 hover:bg-green-600 disabled:opacity-60 text-white font-semibold py-3 rounded-lg transition duration-200"
            >
              {submitting ? 'Submitting...' : 'Submit Beta Application'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default BetaSignup;